import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Field, Input, Select, Btn, ChoiceInput } from "../kit";
import { CATEGORY_LABEL, CUP_TYPES, CUP_SIZES, num } from "../../lib/constants";

const BLANK = { name: "", category: "cup", unit: "pcs", min_stock: "", cup_type: "", cup_size: "", initial_stock: "" };

export function ItemDialog({ open, item, onClose, onSaved }) {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const { data: opt } = useApi("/settings/options", 0);
  useEffect(() => { if (open) setF(item ? { ...item, initial_stock: "" } : BLANK); }, [open, item]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const isCup = f.category === "cup";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, min_stock: Number(f.min_stock) || 0, initial_stock: Number(f.initial_stock) || 0,
      name: isCup && !f.name ? `Cup ${f.cup_type} ${f.cup_size}` : f.name };
    try {
      const { data } = item ? await api.put(`/items/${item.id}`, body) : await api.post("/items", body);
      toast.success("Barang disimpan");
      onSaved(data);
    } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="item-dialog">
        <DialogHeader><DialogTitle>{item ? "Edit Barang" : "Tambah Barang"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Kategori"><Select options={Object.entries(CATEGORY_LABEL)} value={f.category} onChange={set("category")} data-testid="item-category-select" /></Field>
          {isCup && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Jenis cup"><ChoiceInput options={opt?.cup_types || CUP_TYPES} required value={f.cup_type} onChange={(v) => setF({ ...f, cup_type: v })} testid="item-cup-type" placeholder="Contoh: Plastik PP" /></Field>
              <Field label="Ukuran"><ChoiceInput options={opt?.cup_sizes || CUP_SIZES} required value={f.cup_size} onChange={(v) => setF({ ...f, cup_size: v })} testid="item-cup-size" placeholder="Contoh: 8 oz" /></Field>
            </div>
          )}
          <Field label={isCup ? "Nama barang (opsional)" : "Nama barang"}><Input required={!isCup} value={f.name} onChange={set("name")} data-testid="item-name-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Satuan"><Input required value={f.unit} onChange={set("unit")} data-testid="item-unit-input" placeholder="pcs / kg / liter" /></Field>
            <Field label="Stok minimum"><Input type="number" min="0" step="any" value={f.min_stock} onChange={set("min_stock")} data-testid="item-min-input" /></Field>
          </div>
          {!item && <Field label="Stok awal"><Input type="number" min="0" step="any" value={f.initial_stock} onChange={set("initial_stock")} data-testid="item-initial-input" /></Field>}
          <Btn type="submit" loading={busy} className="w-full" data-testid="item-save-btn">Simpan</Btn>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const MODES = {
  in: { title: "Barang Masuk", url: "in", btn: "success", label: "Jumlah masuk" },
  out: { title: "Barang Keluar", url: "out", btn: "danger", label: "Jumlah keluar" },
  opname: { title: "Stok Opname", url: "opname", btn: "dark", label: "Stok fisik sebenarnya" },
};

export function MoveDialog({ mode, item, onClose, onSaved }) {
  const [f, setF] = useState({ qty: "", supplier: "", note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => setF({ qty: "", supplier: "", note: "" }), [mode, item]);
  const m = MODES[mode];
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = mode === "opname" ? { actual: Number(f.qty), note: f.note } : { qty: Number(f.qty), supplier: f.supplier, note: f.note };
    try {
      const { data } = await api.post(`/items/${item.id}/${m.url}`, body);
      toast.success(`${m.title} dicatat`);
      onSaved(data);
    } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!mode && !!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl" data-testid="move-dialog">
        {m && item && <>
          <DialogHeader><DialogTitle>{m.title}</DialogTitle></DialogHeader>
          <div className="p-3 rounded-xl bg-slate-50 text-sm"><b>{item.name}</b><div className="font-mono">Stok sistem: {num(item.stock)} {item.unit}</div></div>
          <form onSubmit={submit} className="space-y-3">
            <Field label={`${m.label} (${item.unit})`}><Input type="number" min={mode === "opname" ? "0" : "0.01"} step="any" required autoFocus value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="move-qty-input" className="text-xl font-mono" /></Field>
            {mode === "in" && <Field label="Supplier"><Input value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} data-testid="move-supplier-input" /></Field>}
            <Field label="Catatan"><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="move-note-input" /></Field>
            <Btn type="submit" variant={m.btn} loading={busy} className="w-full h-14 text-lg" data-testid="move-save-btn">Simpan</Btn>
          </form>
        </>}
      </DialogContent>
    </Dialog>
  );
}
